import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import {
  ArrowLeft,
  BookOpen,
  Play,
  Check,
  Send,
  MessageSquare,
  Edit2,
  Trash2,
  FolderOpen,
} from 'lucide-react'
import Modal from '../ui/Modal'
import Hls from 'hls.js'
// Helper function to format date
const formatDate = (date) => {
  const now = new Date()
  const commentDate = new Date(date)
  const diffInSeconds = Math.floor((now - commentDate) / 1000)

  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minute${
      Math.floor(diffInSeconds / 60) > 1 ? 's' : ''
    } ago`
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hour${
      Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''
    } ago`
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)} day${
      Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''
    } ago`
  if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 2592000)} month${
      Math.floor(diffInSeconds / 2592000) > 1 ? 's' : ''
    } ago`
  return `${Math.floor(diffInSeconds / 31536000)} year${
    Math.floor(diffInSeconds / 31536000) > 1 ? 's' : ''
  } ago`
}

// Set to true to re-enable comment functionality in a later phase
const COMMENTS_ENABLED = false

function StudentLessonView() {
  const { courseId, lessonId } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const [lesson, setLesson] = useState(null)
  const [lessons, setLessons] = useState([])
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const [expandedLessonDescription, setExpandedLessonDescription] =
    useState(false)
  const [completionUpdate, setCompletionUpdate] = useState(0)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [commentToEdit, setCommentToEdit] = useState(null)
  const [editedCommentText, setEditedCommentText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [retakeModalOpen, setRetakeModalOpen] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const commentsEndRef = useRef(null)
  const api = apiService()

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails()
      fetchLessons()
    }
    // eslint-disable-next-line
  }, [courseId])

  useEffect(() => {
    if (lessonId) {
      fetchLessonDetails()
      fetchComments()
    } else {
      // If no lessonId, lesson will be set when lessons are fetched
      setLesson(null)
      setComments([])
    }
    // eslint-disable-next-line
  }, [lessonId])

  useEffect(() => {
    // Scroll to bottom when comments are loaded
    if (comments.length > 0) {
      commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [comments])

  const fetchCourseDetails = async () => {
    try {
      const response = await api.get(`courses/${courseId}`)
      if (response.status === 200) {
        setCourse(response.data.course || response.data)
      }
    } catch (error) {
      toast.error('Failed to fetch course details')
    }
  }

  const fetchLessons = async () => {
    try {
      // Fetch all lessons - students should see all lessons in assigned courses
      const response = await api.get(`courses/${courseId}/lessons`)
      if (response.status === 200) {
        const lessonsData = response.data.lessons || response.data || []
        // Show all lessons for students (both published and draft)
        // Filter out only archived lessons
        const availableLessons = lessonsData.filter(
          (lesson) => lesson.status !== 'archived'
        )
        setLessons(availableLessons)

        // Auto-select lesson if lessonId is provided
        if (lessonId && availableLessons.length > 0) {
          const selected = availableLessons.find((l) => l._id === lessonId)
          if (selected) {
            // Check if this lesson is already completed - if so, redirect to first uncompleted
            const isCompleted = isLessonCompleted(selected._id)
            if (isCompleted) {
              const nextLesson = findNextUncompletedLesson(availableLessons)
              if (nextLesson) {
                navigate(
                  `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                  { replace: true }
                )
                return
              }
            }
            setLesson(selected)
          } else if (availableLessons.length > 0) {
            // If lesson not found, find next uncompleted lesson
            const nextLesson = findNextUncompletedLesson(availableLessons)
            if (nextLesson) {
              navigate(
                `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                { replace: true }
              )
            } else {
              // All lessons completed, go to first lesson (API order = display order)
              navigate(
                `/student/courses/${courseId}/lessons/${availableLessons[0]._id}`,
                { replace: true }
              )
            }
          }
        } else if (availableLessons.length > 0 && !lessonId) {
          // If no lessonId, find next uncompleted lesson
          const nextLesson = findNextUncompletedLesson(availableLessons)
          if (nextLesson) {
            navigate(`/student/courses/${courseId}/lessons/${nextLesson._id}`, {
              replace: true,
            })
          } else {
            // All lessons completed, go to first lesson (API order = display order)
            navigate(`/student/courses/${courseId}/lessons/${availableLessons[0]._id}`, {
              replace: true,
            })
          }
        } else if (availableLessons.length === 0) {
          // No lessons available
          setIsLoading(false)
        }
        setIsLoading(false)
      }
    } catch (error) {
      toast.error('Failed to fetch lessons')
      setIsLoading(false)
    }
  }

  // Find the next uncompleted lesson (use API order = category then lesson order, same as display)
  const findNextUncompletedLesson = (lessonsList) => {
    const completedLessons = getCompletedLessons()
    const inOrder = [...lessonsList]

    const nextLesson = inOrder.find(
      (lesson) => !completedLessons.includes(lesson._id)
    )
    return nextLesson || null
  }

  // Check if all lessons are completed (use API order for consistency)
  const areAllLessonsCompleted = () => {
    if (lessons.length === 0) return false
    const completedLessons = getCompletedLessons()
    return lessons.every((lesson) =>
      completedLessons.includes(String(lesson._id).trim())
    )
  }

  // Reset course progress (retake course)
  const retakeCourse = async () => {
    setIsResetting(true)
    try {
      const userId = localStorage.getItem('userid')
      if (!userId || !courseId) {
        toast.error('Error: Unable to reset course progress')
        setIsResetting(false)
        return
      }

      const courseIdStr = String(courseId).trim()

      // Clear completed lessons
      const completedKey = `completedLessons_${courseIdStr}_${userId}`
      localStorage.removeItem(completedKey)

      // Clear all video positions for this course
      const allKeys = Object.keys(localStorage)
      allKeys.forEach((key) => {
        if (
          key.startsWith(`videoPosition_${courseIdStr}_`) &&
          key.endsWith(`_${userId}`)
        ) {
          localStorage.removeItem(key)
        }
      })

      toast.success('Course progress reset! Starting from the beginning...')

      // Close modal before navigation
      setRetakeModalOpen(false)
      setIsResetting(false)

      // Navigate to first lesson (use API order = same as sidebar)
      if (lessons.length > 0) {
        navigate(
          `/student/courses/${courseId}/lessons/${lessons[0]._id}`,
          { replace: true }
        )
      }

      // Force re-render
      setCompletionUpdate((prev) => prev + 1)

      // Dispatch event to update course list
      window.dispatchEvent(
        new CustomEvent('lessonCompleted', {
          detail: { courseId: courseIdStr, reset: true },
        })
      )
    } catch (error) {
      toast.error('Failed to reset course progress')
      setIsResetting(false)
    }
  }

  const fetchLessonDetails = async () => {
    if (!lessonId) return
    try {
      const response = await api.get(`lessons/${lessonId}`)
      if (response.status === 200) {
        const lessonData = response.data.lesson || response.data
        setLesson(lessonData)
      }
    } catch (error) {
      toast.error('Failed to fetch lesson details')
    }
  }

  const fetchComments = async () => {
    if (!lessonId) return
    try {
      const response = await api.get(`lessons/${lessonId}/comments`)
      if (response.status === 200) {
        const commentsData = response.data.comments || response.data || []
        setComments(Array.isArray(commentsData) ? commentsData : [])
      }
    } catch (error) {
      toast.error('Failed to fetch comments')
      setComments([])
    }
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim()) {
      toast.error('Please enter a comment')
      return
    }

    try {
      setIsSubmittingComment(true)
      const response = await api.post(`lessons/${lessonId}/comments`, {
        comment: newComment.trim(),
      })

      if (response.status === 201) {
        toast.success('Comment added successfully')
        setNewComment('')
        await fetchComments()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add comment')
    } finally {
      setIsSubmittingComment(false)
    }
  }

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return
    }

    try {
      const response = await api.del(`lessons/comments/${commentId}`)
      if (response.status === 200) {
        toast.success('Comment deleted successfully')
        await fetchComments()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete comment')
    }
  }

  const handleEditClick = (comment) => {
    setCommentToEdit(comment)
    setEditedCommentText(comment.comment)
    setEditModalOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!editedCommentText.trim()) {
      toast.error('Comment cannot be empty')
      return
    }

    try {
      setIsSaving(true)
      const response = await api.put(`admin/comments/${commentToEdit._id}`, {
        comment: editedCommentText.trim(),
      })

      if (response.status === 200) {
        toast.success('Comment updated successfully')
        setEditModalOpen(false)
        setCommentToEdit(null)
        setEditedCommentText('')
        await fetchComments()
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update comment')
    } finally {
      setIsSaving(false)
    }
  }

  // Check if current user is admin
  const isAdmin = () => {
    const role = localStorage.getItem('role')
    return role === 'admin' || role === 'subadmin'
  }

  // Get current user ID
  const getCurrentUserId = () => {
    return localStorage.getItem('userid')
  }

  // Get completed lessons from localStorage (user-specific)
  const getCompletedLessons = () => {
    try {
      const userId = localStorage.getItem('userid')
      if (!userId || !courseId) return []

      const courseIdStr = String(courseId).trim()
      const key = `completedLessons_${courseIdStr}_${userId}`
      const completed = localStorage.getItem(key)

      if (completed) {
        const parsed = JSON.parse(completed)
        return Array.isArray(parsed)
          ? parsed.map((id) => String(id).trim())
          : []
      }
      return []
    } catch (error) {
      return []
    }
  }

  // Mark a lesson as completed
  const markLessonAsCompleted = (lessonId) => {
    try {
      const userId = localStorage.getItem('userid')
      if (!userId || !courseId || !lessonId) {
        toast.error('Error: Missing required data to mark lesson as complete')
        return
      }

      const completed = getCompletedLessons()
      const lessonIdStr = String(lessonId).trim()
      const courseIdStr = String(courseId).trim()

      if (!completed.includes(lessonIdStr)) {
        completed.push(lessonIdStr)
        const key = `completedLessons_${courseIdStr}_${userId}`
        localStorage.setItem(key, JSON.stringify(completed))
        setCompletionUpdate((prev) => prev + 1)

        // Dispatch custom event to notify other components
        window.dispatchEvent(
          new CustomEvent('lessonCompleted', {
            detail: { courseId: courseIdStr, lessonId: lessonIdStr },
          })
        )
      }
    } catch (error) {
      toast.error('Failed to mark lesson as complete')
    }
  }

  // Get current user role
  const getCurrentUserRole = () => {
    return localStorage.getItem('role')
  }

  // Check if a lesson is completed
  const isLessonCompleted = (lessonId) => {
    const completed = getCompletedLessons()
    const lessonIdStr = String(lessonId).trim()
    return completed.includes(lessonIdStr)
  }

  // Helper to normalize ID (handle objects, strings, etc.)
  const normalizeId = (id) => {
    if (!id) return ''
    if (typeof id === 'string') return id.trim()
    if (typeof id === 'object' && id.toString) return id.toString().trim()
    return String(id).trim()
  }

  // Save video playback position to localStorage
  const saveVideoPosition = (lessonId, currentTime) => {
    try {
      const userId = localStorage.getItem('userid')
      const courseIdStr = normalizeId(courseId)
      const lessonIdStr = normalizeId(lessonId)
      const userIdStr = normalizeId(userId)

      if (!userIdStr || !courseIdStr || !lessonIdStr) {
        return
      }

      const key = `videoPosition_${courseIdStr}_${lessonIdStr}_${userIdStr}`
      // Save position if video has been watched for at least 3 seconds (reduced from 5)
      if (currentTime >= 3) {
        const positionData = { time: currentTime, timestamp: Date.now() }
        localStorage.setItem(key, JSON.stringify(positionData))
      }
    } catch (error) {
      // Silently fail - video position saving is not critical
    }
  }

  // Get saved video playback position from localStorage
  const getVideoPosition = (lessonId) => {
    try {
      const userId = localStorage.getItem('userid')
      const courseIdStr = normalizeId(courseId)
      const lessonIdStr = normalizeId(lessonId)
      const userIdStr = normalizeId(userId)

      if (!userIdStr || !courseIdStr || !lessonIdStr) {
        return 0
      }

      const key = `videoPosition_${courseIdStr}_${lessonIdStr}_${userIdStr}`
      const saved = localStorage.getItem(key)

      if (saved) {
        const data = JSON.parse(saved)
        // Only restore if saved within last 30 days
        const daysSinceSave =
          (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24)
        if (daysSinceSave < 30) {
          return data.time
        } else {
          // Remove expired position
          localStorage.removeItem(key)
        }
      } else {
        // Try to find a matching key with different format
        const allKeys = Object.keys(localStorage).filter((k) =>
          k.startsWith('videoPosition_')
        )
        const matchingKey = allKeys.find(
          (k) =>
            k.includes(courseIdStr) &&
            k.includes(userIdStr) &&
            (k.includes(lessonIdStr) || lessonIdStr.includes(k.split('_')[2]))
        )
        if (matchingKey) {
          const foundData = localStorage.getItem(matchingKey)
          if (foundData) {
            const data = JSON.parse(foundData)
            return data.time
          }
        }
      }
      return 0
    } catch (error) {
      return 0
    }
  }

  const handleLessonClick = (lesson, index) => {
    navigate(`/student/courses/${courseId}/lessons/${lesson._id}`)
  }

  const getVideoUrl = (videoUrl) => {
    if (!videoUrl) return null
    if (videoUrl.startsWith('/uploads/')) {
      return `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${videoUrl}`
    }
    return videoUrl
  }

  const isYouTubeUrl = (url) => {
    if (!url) return false
    // eslint-disable-next-line no-useless-escape
    return /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/.test(
      url
    )
  }

  const isVimeoUrl = (url) => {
    if (!url) return false
    return /vimeo\.com\/(?:.*\/)?(\d+)/.test(url)
  }

  const getYouTubeEmbedUrl = (url) => {
    // eslint-disable-next-line no-useless-escape
    const regExp =
      /^.*(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    const match = url.match(regExp)
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`
    }
    return url
  }

  const getVimeoEmbedUrl = (url) => {
    const regExp = /vimeo\.com\/(?:.*\/)?(\d+)/
    const match = url.match(regExp)
    if (match && match[1]) {
      return `https://player.vimeo.com/video/${match[1]}`
    }
    return url
  }

  const isCloudflareStreamUrl = (url) => {
    if (!url) return false
    return /cloudflarestream\.com|videodelivery\.net/.test(url)
  }

  const isCloudflareEmbedUrl = (url) => {
    if (!url) return false
    return /iframe\.videodelivery\.net/.test(url)
  }

  const isHlsUrl = (url) => {
    if (!url) return false
    return /\.m3u8/.test(url)
  }

  const getCloudflareHlsUrl = (url) => {
    // Convert Cloudflare Stream URL to HLS manifest URL
    // Format: https://iframe.videodelivery.net/xxxxx -> https://customer-{account}.cloudflarestream.com/xxxxx/manifest/video.m3u8
    // We need to extract the video ID and construct the HLS URL
    if (url.includes('videodelivery.net')) {
      const match = url.match(/videodelivery\.net\/([^\/\?]+)/)
      if (match && match[1]) {
        const videoId = match[1]
        // Try to get account ID from environment or use default pattern
        // Cloudflare Stream HLS URLs typically follow this pattern
        // For now, we'll try the direct manifest URL pattern
        return `https://customer-afe8604b478b9c99fd36696c02b9f708.cloudflarestream.com/${videoId}/manifest/video.m3u8`
      }
    }
    if (url.includes('cloudflarestream.com')) {
      const match = url.match(/cloudflarestream\.com\/([^\/\?]+)/)
      if (match && match[1]) {
        const videoId = match[1]
        // Extract account ID from the URL if present
        const accountMatch = url.match(
          /customer-([^\.]+)\.cloudflarestream\.com/
        )
        if (accountMatch && accountMatch[1]) {
          return `https://customer-${accountMatch[1]}.cloudflarestream.com/${videoId}/manifest/video.m3u8`
        }
        // Fallback to default account ID from env
        return `https://customer-afe8604b478b9c99fd36696c02b9f708.cloudflarestream.com/${videoId}/manifest/video.m3u8`
      }
    }
    return null
  }

  const getCloudflareEmbedUrl = (url) => {
    // Extract video ID from Cloudflare Stream URL
    // Format: https://customer-xxxxx.cloudflarestream.com/xxxxx/video.mp4
    // or https://iframe.videodelivery.net/xxxxx
    if (url.includes('videodelivery.net')) {
      const match = url.match(/videodelivery\.net\/([^\/\?]+)/)
      if (match && match[1]) {
        return `https://iframe.videodelivery.net/${match[1]}`
      }
    }
    if (url.includes('cloudflarestream.com')) {
      const match = url.match(/cloudflarestream\.com\/([^\/\?]+)/)
      if (match && match[1]) {
        return `https://iframe.videodelivery.net/${match[1]}`
      }
    }
    return url
  }

  // HLS Player Component for Cloudflare HLS streams
  const CloudflareHLSPlayer = ({ src, onEnded, lessonId, onMarkComplete }) => {
    const videoElementRef = useRef(null)
    const hlsInstanceRef = useRef(null)
    const savePositionTimeoutRef = useRef(null)
    const completionCheckedRef = useRef(false)

    useEffect(() => {
      const video = videoElementRef.current
      if (!video || !src || !lessonId) return

      // Reset completion check when lesson changes
      completionCheckedRef.current = false

      // Restore saved position
      const savedPosition = getVideoPosition(lessonId)
      let positionRestored = false

      // Cleanup previous HLS instance
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy()
        hlsInstanceRef.current = null
      }

      // Save position periodically and check for completion
      const handleTimeUpdate = () => {
        if (video.currentTime && video.duration && lessonId) {
          const currentTime = video.currentTime
          const duration = video.duration

          // Don't mark completion during timeupdate - let the video play to 100%
          // Only mark completion when the actual 'ended' event fires
          // This ensures the video plays completely without interruption

          // Save position every 5 seconds
          if (currentTime >= 3) {
            const currentTimeFloor = Math.floor(currentTime)
            if (!savePositionTimeoutRef.current || currentTimeFloor % 5 === 0) {
              if (savePositionTimeoutRef.current) {
                clearTimeout(savePositionTimeoutRef.current)
              }
              savePositionTimeoutRef.current = setTimeout(() => {
                saveVideoPosition(lessonId, currentTime)
              }, 1000)
            }
          }
        }
      }

      // Save position on pause and check for completion
      const handlePause = () => {
        if (video.currentTime && video.duration && lessonId) {
          const currentTime = video.currentTime
          const duration = video.duration
          const completionPercentage = duration > 0 ? currentTime / duration : 0
          const secondsRemaining = duration - currentTime

          // Check completion when paused near the end
          if (
            (completionPercentage >= 0.85 || secondsRemaining <= 15) &&
            !completionCheckedRef.current
          ) {
            completionCheckedRef.current = true
            if (onMarkComplete) {
              onMarkComplete(lessonId)
            } else if (onEnded) {
              onEnded()
            }
          }

          if (currentTime >= 3) {
            saveVideoPosition(lessonId, currentTime)
          }
        }
      }

      // Save position before page unload
      const handleBeforeUnload = () => {
        if (video.currentTime && lessonId) {
          saveVideoPosition(lessonId, video.currentTime)
        }
      }

      // Function to attach event listeners (removes old ones first to prevent duplicates)
      const attachEventListeners = () => {
        // Remove any existing listeners first
        video.removeEventListener('timeupdate', handleTimeUpdate)
        video.removeEventListener('pause', handlePause)
        // Add listeners
        video.addEventListener('timeupdate', handleTimeUpdate)
        video.addEventListener('pause', handlePause)
        window.addEventListener('beforeunload', handleBeforeUnload)
      }

      // Attach listeners immediately
      attachEventListeners()

      if (Hls.isSupported()) {
        // Use HLS.js for browsers that don't support native HLS
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
        })
        hls.loadSource(src)
        hls.attachMedia(video)
        hlsInstanceRef.current = hls

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // Video is ready to play - restore position
          if (savedPosition > 0 && !positionRestored) {
            video.currentTime = savedPosition
            positionRestored = true
            // Also try after video is ready to play
            setTimeout(() => {
              if (video.currentTime < savedPosition - 1) {
                video.currentTime = savedPosition
              }
            }, 1000)
          }
          // Ensure event listeners are attached after manifest is parsed
          attachEventListeners()
        })

        // Also restore when video metadata is loaded
        video.addEventListener('loadedmetadata', () => {
          if (savedPosition > 0 && !positionRestored) {
            video.currentTime = savedPosition
            positionRestored = true
          }
          // Ensure event listeners are attached
          attachEventListeners()
        })

        video.addEventListener('loadeddata', () => {
          attachEventListeners()
        })

        video.addEventListener('canplay', () => {
          attachEventListeners()
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError()
                break
              default:
                hls.destroy()
                break
            }
          }
        })

        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate)
          video.removeEventListener('pause', handlePause)
          video.removeEventListener('play', () => {})
          window.removeEventListener('beforeunload', handleBeforeUnload)
          if (savePositionTimeoutRef.current) {
            clearTimeout(savePositionTimeoutRef.current)
          }
          if (hlsInstanceRef.current) {
            hlsInstanceRef.current.destroy()
            hlsInstanceRef.current = null
          }
        }
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = src
        video.addEventListener('loadedmetadata', () => {
          if (savedPosition > 0 && !positionRestored) {
            video.currentTime = savedPosition
            positionRestored = true
          }
          attachEventListeners()
        })
        video.addEventListener('loadeddata', () => {
          attachEventListeners()
        })
        video.addEventListener('canplay', () => {
          attachEventListeners()
        })

        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate)
          video.removeEventListener('pause', handlePause)
          video.removeEventListener('play', () => {})
          video.removeEventListener('loadedmetadata', () => {})
          window.removeEventListener('beforeunload', handleBeforeUnload)
          if (savePositionTimeoutRef.current) {
            clearTimeout(savePositionTimeoutRef.current)
          }
        }
      } else {
        return () => {
          video.removeEventListener('timeupdate', handleTimeUpdate)
          video.removeEventListener('pause', handlePause)
          video.removeEventListener('play', () => {})
          window.removeEventListener('beforeunload', handleBeforeUnload)
          if (savePositionTimeoutRef.current) {
            clearTimeout(savePositionTimeoutRef.current)
          }
        }
      }
    }, [src, lessonId])

    return (
      <video
        ref={videoElementRef}
        controls
        className="w-full h-full"
        style={{ maxHeight: '600px' }}
        crossOrigin="anonymous"
        preload="metadata"
        onEnded={() => {
          // Mark as completed when video actually ends
          completionCheckedRef.current = true
          if (onMarkComplete) {
            onMarkComplete(lessonId)
          }
          // Always call onEnded callback for navigation
          if (onEnded) {
            onEnded()
          }
        }}
        onError={() => {
          // HLS video load error - handled silently
        }}
      >
        Your browser does not support HLS video playback.
        <p className="text-white p-4">
          If the video doesn't play, check the browser console for errors.
          <br />
          Video URL: {src}
        </p>
      </video>
    )
  }

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (isLoading) {
    return (
      <div className="bg-white border rounded-[10px] border-[#F0F0F0] p-8">
        <div className="text-center">Loading lesson...</div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="bg-white border rounded-[10px] border-[#F0F0F0] p-8">
        <div className="text-center text-red-500">Course not found</div>
      </div>
    )
  }

  // Use API order: backend returns lessons sorted by category order then lesson order.
  // Do not re-sort by global lesson order only, or the same category would appear multiple times.
  const sortedLessons = [...lessons]

  // Group lessons by category for display (matches admin: one section per category, in category order)
  const getSections = () => {
    const sections = []
    let currentSection = null
    sortedLessons.forEach((l) => {
      const catId = l.category?._id?.toString() || l.category || null
      const catTitle = l.category?.title || 'Uncategorized'
      if (!currentSection || currentSection.id !== catId) {
        currentSection = { id: catId, title: catTitle, lessons: [] }
        sections.push(currentSection)
      }
      currentSection.lessons.push(l)
    })
    return sections
  }
  const sections = getSections()

  return (
    <div className="bg-[#FCFCFC] min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-[#F0F0F0] px-5 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-[#030303]">{course.title}</h1>
          <button
            onClick={() => navigate('/student/courses')}
            className="px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors text-sm font-medium flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Courses</span>
          </button>
        </div>
      </div>

      {/* Main Content - Udemy Style Layout */}
      <div className="flex flex-col lg:flex-row gap-0">
        {/* Left Side - Video Player Area */}
        <div className="lg:w-2/3 bg-white">
          {lesson ? (
            <div className="p-6">
              {/* Video Player */}
              <div
                className="bg-black rounded-lg overflow-hidden mb-6"
                style={{ aspectRatio: '16/9' }}
              >
                {lesson.videoUrl ? (
                  (() => {
                    const videoUrl = lesson.videoUrl
                    const fullUrl = getVideoUrl(videoUrl)

                    if (isYouTubeUrl(videoUrl)) {
                      const YouTubePlayer = () => {
                        const containerRef = useRef(null)
                        const playerRef = useRef(null)
                        const apiReadyRef = useRef(false)

                        useEffect(() => {
                          const videoId =
                            getYouTubeEmbedUrl(videoUrl).match(
                              /embed\/([^?]+)/
                            )?.[1]
                          if (!videoId || !containerRef.current) return

                          const initPlayer = () => {
                            if (
                              window.YT &&
                              window.YT.Player &&
                              containerRef.current
                            ) {
                              playerRef.current = new window.YT.Player(
                                containerRef.current,
                                {
                                  videoId: videoId,
                                  playerVars: {
                                    enablejsapi: 1,
                                    playsinline: 1,
                                  },
                                  events: {
                                    onReady: (event) => {
                                      const savedPosition = getVideoPosition(
                                        lesson._id
                                      )
                                      if (savedPosition > 0) {
                                        event.target.seekTo(savedPosition, true)
                                      }
                                    },
                                    onStateChange: (event) => {
                                      // Save position when paused
                                      if (
                                        event.data ===
                                        window.YT.PlayerState.PAUSED
                                      ) {
                                        const currentTime =
                                          event.target.getCurrentTime()
                                        if (currentTime >= 5) {
                                          saveVideoPosition(
                                            lesson._id,
                                            currentTime
                                          )
                                        }
                                      }
                                    },
                                  },
                                }
                              )
                              apiReadyRef.current = true
                            }
                          }

                          // Load YouTube IFrame API if not already loaded
                          if (!window.YT) {
                            const tag = document.createElement('script')
                            tag.src = 'https://www.youtube.com/iframe_api'
                            const firstScriptTag =
                              document.getElementsByTagName('script')[0]
                            firstScriptTag.parentNode.insertBefore(
                              tag,
                              firstScriptTag
                            )

                            window.onYouTubeIframeAPIReady = () => {
                              initPlayer()
                            }
                          } else if (window.YT.Player) {
                            // API already loaded
                            initPlayer()
                          } else {
                            // Wait for API to be ready
                            const checkInterval = setInterval(() => {
                              if (window.YT && window.YT.Player) {
                                clearInterval(checkInterval)
                                initPlayer()
                              }
                            }, 100)

                            return () => clearInterval(checkInterval)
                          }

                          // Save position periodically
                          const interval = setInterval(() => {
                            if (playerRef.current && apiReadyRef.current) {
                              try {
                                const currentTime =
                                  playerRef.current.getCurrentTime()
                                if (currentTime && currentTime >= 5) {
                                  saveVideoPosition(lesson._id, currentTime)
                                }
                              } catch (e) {
                                // Player might not be ready
                              }
                            }
                          }, 10000) // Save every 10 seconds

                          return () => {
                            clearInterval(interval)
                            if (
                              playerRef.current &&
                              playerRef.current.destroy
                            ) {
                              try {
                                playerRef.current.destroy()
                              } catch (e) {
                                // Ignore destroy errors
                              }
                            }
                          }
                        }, [lesson._id, videoUrl])

                        return (
                          <div
                            ref={containerRef}
                            key={lesson._id}
                            className="w-full h-full"
                            style={{ minHeight: '400px' }}
                          />
                        )
                      }
                      return <YouTubePlayer key={lesson._id} />
                    }

                    if (isVimeoUrl(videoUrl)) {
                      const VimeoPlayer = () => {
                        const iframeRef = useRef(null)
                        const playerRef = useRef(null)

                        useEffect(() => {
                          const videoId =
                            getVimeoEmbedUrl(videoUrl).match(
                              /video\/(\d+)/
                            )?.[1]
                          if (!videoId || !iframeRef.current) return

                          const initPlayer = () => {
                            if (
                              window.Vimeo &&
                              window.Vimeo.Player &&
                              iframeRef.current
                            ) {
                              playerRef.current = new window.Vimeo.Player(
                                iframeRef.current
                              )

                              playerRef.current
                                .ready()
                                .then(() => {
                                  const savedPosition = getVideoPosition(
                                    lesson._id
                                  )
                                  if (savedPosition > 0) {
                                    playerRef.current.setCurrentTime(
                                      savedPosition
                                    )
                                  }
                                })
                                .catch(() => {
                                  // Vimeo player ready error - handled silently
                                })

                              // Save position on pause
                              playerRef.current.on('pause', () => {
                                playerRef.current
                                  .getCurrentTime()
                                  .then((time) => {
                                    if (time >= 5) {
                                      saveVideoPosition(lesson._id, time)
                                    }
                                  })
                                  .catch(() => {
                                    // Error getting Vimeo current time - handled silently
                                  })
                              })
                            }
                          }

                          // Load Vimeo Player API if not already loaded
                          if (!window.Vimeo) {
                            const tag = document.createElement('script')
                            tag.src = 'https://player.vimeo.com/api/player.js'
                            const firstScriptTag =
                              document.getElementsByTagName('script')[0]
                            firstScriptTag.parentNode.insertBefore(
                              tag,
                              firstScriptTag
                            )

                            tag.onload = () => {
                              initPlayer()
                            }
                          } else {
                            // API already loaded
                            initPlayer()
                          }

                          // Save position periodically
                          const interval = setInterval(() => {
                            if (playerRef.current) {
                              playerRef.current
                                .getCurrentTime()
                                .then((time) => {
                                  if (time && time >= 5) {
                                    saveVideoPosition(lesson._id, time)
                                  }
                                })
                                .catch((err) => {
                                  // Player might not be ready
                                })
                            }
                          }, 10000) // Save every 10 seconds

                          return () => {
                            clearInterval(interval)
                          }
                        }, [lesson._id, videoUrl])

                        return (
                          <iframe
                            ref={iframeRef}
                            key={lesson._id}
                            src={getVimeoEmbedUrl(videoUrl)}
                            className="w-full h-full"
                            style={{ minHeight: '400px' }}
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={lesson.title}
                          />
                        )
                      }
                      return <VimeoPlayer key={lesson._id} />
                    }

                    // Cloudflare Stream embed URL (only if it's specifically an embed URL, not a direct MP4)
                    // Note: We use the embed player for Cloudflare Stream as HLS conversion has CORS/access issues
                    if (
                      isCloudflareEmbedUrl(videoUrl) ||
                      (isCloudflareStreamUrl(videoUrl) &&
                        !isHlsUrl(videoUrl) &&
                        !fullUrl.includes('.mp4') &&
                        !fullUrl.includes('video.mp4'))
                    ) {
                      const CloudflareEmbedPlayer = () => {
                        const playerContainerRef = useRef(null)
                        const playerRef = useRef(null)
                        const iframeRef = useRef(null)
                        const [embedSrc, setEmbedSrc] = useState('')
                        const [useStreamPlayer, setUseStreamPlayer] =
                          useState(false)
                        const completionCheckedRef = useRef(false)
                        const navigationTriggeredRef = useRef(false)
                        const videoDurationRef = useRef(0)

                        useEffect(() => {
                          // Reset completion check and navigation when lesson changes
                          completionCheckedRef.current = false
                          navigationTriggeredRef.current = false
                          videoDurationRef.current = 0

                          // Extract video ID from URL
                          const videoIdMatch =
                            videoUrl.match(/videodelivery\.net\/([^\/\?]+)/) ||
                            videoUrl.match(/cloudflarestream\.com\/([^\/\?]+)/)
                          const videoId = videoIdMatch ? videoIdMatch[1] : null

                          // Try to load Cloudflare Stream Player JavaScript API
                          if (videoId && !window.Stream) {
                            const script = document.createElement('script')
                            script.src =
                              'https://embed.videodelivery.net/embed/sdk.latest.js'
                            script.async = true
                            script.onload = () => {
                              setUseStreamPlayer(true)
                            }
                            script.onerror = () => {
                              setUseStreamPlayer(false)
                            }
                            document.head.appendChild(script)
                          } else if (window.Stream && videoId) {
                            setUseStreamPlayer(true)
                          } else {
                            setUseStreamPlayer(false)
                          }

                          // Get saved position
                          const savedPosition = getVideoPosition(lesson._id)
                          let embedUrl = getCloudflareEmbedUrl(videoUrl)

                          // Cloudflare Stream supports 't' parameter for starting at specific time (in seconds)
                          if (savedPosition > 0) {
                            const separator = embedUrl.includes('?') ? '&' : '?'
                            embedUrl = `${embedUrl}${separator}t=${Math.round(
                              savedPosition
                            )}`
                          }

                          setEmbedSrc(embedUrl)

                          // Initialize Stream Player if available
                          if (
                            useStreamPlayer &&
                            window.Stream &&
                            videoId &&
                            playerContainerRef.current &&
                            !playerRef.current
                          ) {
                            try {
                              const stream = new window.Stream(
                                playerContainerRef.current,
                                {
                                  videoId: videoId,
                                  autoplay: false,
                                  controls: true,
                                  startTime:
                                    savedPosition > 0
                                      ? savedPosition
                                      : undefined,
                                }
                              )

                              playerRef.current = stream

                              // Listen to player events
                              stream.addEventListener('pause', () => {
                                if (stream.currentTime && stream.duration) {
                                  // Save position on pause, but don't mark completion
                                  if (stream.currentTime >= 3) {
                                    saveVideoPosition(
                                      lesson._id,
                                      stream.currentTime
                                    )
                                  }
                                }
                              })

                              stream.addEventListener('timeupdate', () => {
                                if (stream.currentTime && stream.duration) {
                                  videoDurationRef.current = stream.duration
                                  // Don't check for completion during timeupdate - let video play to 100%
                                  if (stream.currentTime >= 5) {
                                    saveVideoPosition(
                                      lesson._id,
                                      stream.currentTime
                                    )
                                  }
                                }
                              })

                              stream.addEventListener('ended', () => {
                                // Mark as completed when video actually ends
                                if (!completionCheckedRef.current) {
                                  const isCompleted = isLessonCompleted(
                                    lesson._id
                                  )
                                  if (!isCompleted) {
                                    completionCheckedRef.current = true
                                    markLessonAsCompleted(lesson._id)
                                    toast.success('Lesson completed!')
                                  } else {
                                    completionCheckedRef.current = true
                                  }
                                }

                                // Navigate to next uncompleted lesson after a delay (display order)
                                setTimeout(() => {
                                  const nextLesson =
                                    findNextUncompletedLesson(lessons)
                                  if (nextLesson) {
                                    navigate(
                                      `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                                      { replace: true }
                                    )
                                  }
                                }, 2000)
                              })

                              stream.addEventListener('loadedmetadata', () => {
                                if (stream.duration) {
                                  videoDurationRef.current = stream.duration
                                }
                              })
                            } catch (error) {
                              setUseStreamPlayer(false)
                            }
                          }

                          // Function to check and mark completion
                          const checkAndMarkCompletion = (
                            currentTime,
                            duration
                          ) => {
                            if (!duration || duration <= 0) return

                            const completionPercentage =
                              duration > 0 ? currentTime / duration : 0
                            const secondsRemaining = duration - currentTime
                            // Only mark as complete when very close to the end (98% or within 2 seconds)
                            // Don't navigate here - let the 'ended' event handle navigation
                            // Video will continue playing until it reaches 100%
                            const shouldMarkComplete =
                              (completionPercentage >= 0.98 ||
                                secondsRemaining <= 2) &&
                              !completionCheckedRef.current

                            if (shouldMarkComplete) {
                              const isCompleted = isLessonCompleted(lesson._id)

                              if (!isCompleted) {
                                completionCheckedRef.current = true
                                markLessonAsCompleted(lesson._id)
                                toast.success('Lesson completed!')
                                // Don't navigate here - wait for video to actually end
                                // Video will continue playing until it reaches 100%
                              } else {
                                completionCheckedRef.current = true
                              }
                            }
                          }

                          // Track current time from propertyChange messages
                          let lastKnownCurrentTime = 0
                          let lastKnownDuration = 0

                          // Listen for messages from Cloudflare player to save position and track completion
                          const handleMessage = (event) => {
                            // Verify message is from Cloudflare
                            if (
                              !event.origin.includes('videodelivery.net') &&
                              !event.origin.includes('cloudflarestream.com')
                            ) {
                              return
                            }

                            if (event.data && typeof event.data === 'object') {
                              const messageType =
                                event.data.__privateUnstableMessageType

                              // Handle propertyChange messages (currentTime, duration, etc.)
                              if (messageType === 'propertyChange') {
                                const property = event.data.property
                                const value = event.data.value

                                if (
                                  property === 'currentTime' &&
                                  typeof value === 'number'
                                ) {
                                  lastKnownCurrentTime = value

                                  // Don't check for completion during timeupdate - let video play to 100%
                                  // Completion will be marked when the 'ended' event fires

                                  // Save position
                                  if (value >= 5) {
                                    saveVideoPosition(lesson._id, value)
                                  }
                                } else if (
                                  property === 'duration' &&
                                  typeof value === 'number' &&
                                  value > 0
                                ) {
                                  lastKnownDuration = value
                                  videoDurationRef.current = value
                                } else if (
                                  property === 'ended' &&
                                  value === true
                                ) {
                                  // Video ended
                                  // Mark as completed if not already done
                                  if (!completionCheckedRef.current) {
                                    const isCompleted = isLessonCompleted(
                                      lesson._id
                                    )
                                    if (!isCompleted) {
                                      completionCheckedRef.current = true
                                      markLessonAsCompleted(lesson._id)
                                      toast.success('Lesson completed!')
                                    } else {
                                      completionCheckedRef.current = true
                                    }
                                  }

                                  // Always navigate when video ends (only once)
                                  if (!navigationTriggeredRef.current) {
                                    navigationTriggeredRef.current = true

                                    // Navigate to next uncompleted lesson after a delay (let user see completion message)
                                    setTimeout(() => {
                                      const nextLesson =
                                        findNextUncompletedLesson(lessons)
                                      if (nextLesson) {
                                        navigate(
                                          `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                                          { replace: true }
                                        )
                                      }
                                    }, 2000)
                                  }
                                }
                              }

                              // Handle event messages (timeupdate, pause, ended)
                              if (messageType === 'event') {
                                const eventName = event.data.eventName

                                if (eventName === 'timeupdate') {
                                  // Use last known currentTime from propertyChange
                                  // Don't check for completion during timeupdate - let video play to 100%
                                  if (lastKnownCurrentTime >= 5) {
                                    saveVideoPosition(
                                      lesson._id,
                                      lastKnownCurrentTime
                                    )
                                  }
                                } else if (eventName === 'pause') {
                                  // Save position on pause, but don't mark completion
                                  if (lastKnownCurrentTime >= 3) {
                                    saveVideoPosition(
                                      lesson._id,
                                      lastKnownCurrentTime
                                    )
                                  }
                                } else if (eventName === 'ended') {
                                  // Mark as completed if not already done
                                  if (!completionCheckedRef.current) {
                                    const isCompleted = isLessonCompleted(
                                      lesson._id
                                    )
                                    if (!isCompleted) {
                                      completionCheckedRef.current = true
                                      markLessonAsCompleted(lesson._id)
                                      toast.success('Lesson completed!')
                                    } else {
                                      completionCheckedRef.current = true
                                    }
                                  }

                                  // Always navigate when video ends (only once)
                                  if (!navigationTriggeredRef.current) {
                                    navigationTriggeredRef.current = true

                                    // Navigate to next uncompleted lesson after a delay (let user see completion message)
                                    setTimeout(() => {
                                      const nextLesson =
                                        findNextUncompletedLesson(lessons)
                                      if (nextLesson) {
                                        navigate(
                                          `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                                          { replace: true }
                                        )
                                      }
                                    }, 2000)
                                  }
                                }
                              }
                            }
                          }

                          window.addEventListener('message', handleMessage)

                          // Track video watch time as a fallback for completion detection
                          let totalWatchTime = 0
                          let lastCheckTime = Date.now()
                          const watchStartTime = Date.now()

                          // Try to periodically get current time and check completion (if Cloudflare player supports it)
                          const saveInterval = setInterval(() => {
                            if (
                              iframeRef.current &&
                              iframeRef.current.contentWindow
                            ) {
                              try {
                                // Request current time and duration from Cloudflare player
                                iframeRef.current.contentWindow.postMessage(
                                  {
                                    method: 'getCurrentTime',
                                  },
                                  '*'
                                )
                                iframeRef.current.contentWindow.postMessage(
                                  {
                                    method: 'getDuration',
                                  },
                                  '*'
                                )

                                // Also try to access the video element directly if possible
                                try {
                                  const iframe = iframeRef.current
                                  if (iframe && iframe.contentDocument) {
                                    const video =
                                      iframe.contentDocument.querySelector(
                                        'video'
                                      )
                                    if (video) {
                                      const currentTime = video.currentTime
                                      const duration = video.duration

                                      if (duration > 0) {
                                        checkAndMarkCompletion(
                                          currentTime,
                                          duration
                                        )
                                        if (currentTime >= 5) {
                                          saveVideoPosition(
                                            lesson._id,
                                            currentTime
                                          )
                                        }
                                      }
                                    }
                                  }
                                } catch (e) {
                                  // Cross-origin restrictions - can't access iframe content
                                  // This is expected for Cloudflare Stream
                                }
                              } catch (e) {
                                // Ignore errors - player might not support this
                              }
                            }

                            // Fallback: Track total watch time and estimate completion
                            // If user has been on page for a long time, they might have watched the video
                            const now = Date.now()
                            const timeSinceLastCheck =
                              (now - lastCheckTime) / 1000 // seconds
                            lastCheckTime = now

                            // If we have a duration estimate and user has been watching, check completion
                            if (videoDurationRef.current > 0) {
                              totalWatchTime += timeSinceLastCheck
                              const estimatedCompletion =
                                totalWatchTime / videoDurationRef.current

                              if (
                                estimatedCompletion >= 0.85 &&
                                !completionCheckedRef.current
                              ) {
                                const isCompleted = isLessonCompleted(
                                  lesson._id
                                )
                                if (!isCompleted) {
                                  completionCheckedRef.current = true
                                  markLessonAsCompleted(lesson._id)
                                  toast.success('Lesson completed!')
                                } else {
                                  completionCheckedRef.current = true
                                }
                              }
                            }
                          }, 3000) // Check every 3 seconds for completion

                          return () => {
                            window.removeEventListener('message', handleMessage)
                            clearInterval(saveInterval)
                          }
                        }, [lesson._id, videoUrl])

                        return (
                          <iframe
                            ref={iframeRef}
                            key={lesson._id}
                            src={embedSrc || getCloudflareEmbedUrl(videoUrl)}
                            className="w-full h-full"
                            style={{ minHeight: '400px' }}
                            frameBorder="0"
                            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                            allowFullScreen
                            title={lesson.title}
                          />
                        )
                      }
                      return <CloudflareEmbedPlayer key={lesson._id} />
                    }

                    // HLS stream (including Cloudflare HLS)
                    if (isHlsUrl(fullUrl)) {
                      return (
                        <CloudflareHLSPlayer
                          key={lesson._id}
                          src={fullUrl}
                          lessonId={lesson._id}
                          onMarkComplete={(lessonId) => {
                            if (!isLessonCompleted(lessonId)) {
                              markLessonAsCompleted(lessonId)
                              toast.success('Lesson completed!')
                            }
                          }}
                          onEnded={() => {
                            // Mark as completed if not already done
                            if (!isLessonCompleted(lesson._id)) {
                              markLessonAsCompleted(lesson._id)
                              toast.success('Lesson completed!')
                            }

                            // Navigate to next uncompleted lesson after a delay
                            setTimeout(() => {
                              const nextLesson =
                                findNextUncompletedLesson(lessons)
                              if (nextLesson) {
                                navigate(
                                  `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                                  { replace: true }
                                )
                              }
                            }, 2000)
                          }}
                        />
                      )
                    }

                    // Direct video file URL (including Cloudflare direct MP4)
                    // Also handle Cloudflare Stream URLs that can be converted to direct MP4
                    const VideoPlayer = () => {
                      const videoRef = useRef(null)
                      const savePositionTimeoutRef = useRef(null)
                      const positionRestoredRef = useRef(false)
                      const completionCheckedRef = useRef(false)

                      useEffect(() => {
                        const video = videoRef.current
                        if (!video) {
                          return
                        }

                        // Reset completion check when lesson changes
                        completionCheckedRef.current = false

                        // Restore saved position
                        const savedPosition = getVideoPosition(lesson._id)

                        const handleLoadedMetadata = () => {
                          if (
                            savedPosition > 0 &&
                            !positionRestoredRef.current
                          ) {
                            video.currentTime = savedPosition
                            positionRestoredRef.current = true
                            // Also try to set it after a short delay in case it gets reset
                            setTimeout(() => {
                              if (video.currentTime < savedPosition - 1) {
                                video.currentTime = savedPosition
                              }
                            }, 500)
                            setTimeout(() => {
                              if (video.currentTime < savedPosition - 1) {
                                video.currentTime = savedPosition
                              }
                            }, 1500)
                          }
                        }

                        // Also try to restore when video can play
                        const handleCanPlay = () => {
                          if (
                            savedPosition > 0 &&
                            !positionRestoredRef.current
                          ) {
                            video.currentTime = savedPosition
                            positionRestoredRef.current = true
                          } else if (
                            savedPosition > 0 &&
                            video.currentTime < savedPosition - 1
                          ) {
                            video.currentTime = savedPosition
                          }
                        }

                        // Try to restore when video is ready
                        const handleLoadedData = () => {
                          if (
                            savedPosition > 0 &&
                            !positionRestoredRef.current
                          ) {
                            video.currentTime = savedPosition
                            positionRestoredRef.current = true
                          }
                        }

                        // Save position periodically and check for completion
                        let lastSavedTime = 0
                        const handleTimeUpdate = () => {
                          if (video.currentTime && video.duration) {
                            const currentTime = video.currentTime
                            const duration = video.duration

                            // Don't mark completion during timeupdate - let video play to 100%
                            // Completion will be marked when the 'ended' event fires

                            // Save position every 5 seconds or if time changed significantly
                            if (
                              currentTime >= 3 &&
                              (currentTime - lastSavedTime >= 5 ||
                                Math.floor(currentTime) % 5 === 0)
                            ) {
                              if (savePositionTimeoutRef.current) {
                                clearTimeout(savePositionTimeoutRef.current)
                              }
                              savePositionTimeoutRef.current = setTimeout(
                                () => {
                                  saveVideoPosition(lesson._id, currentTime)
                                  lastSavedTime = currentTime
                                },
                                500
                              )
                            }
                          }
                        }

                        // Save position on pause and check for completion
                        const handlePause = () => {
                          if (video.currentTime && video.duration) {
                            const currentTime = video.currentTime
                            const duration = video.duration
                            const completionPercentage =
                              duration > 0 ? currentTime / duration : 0
                            const secondsRemaining = duration - currentTime

                            // Don't mark completion on pause - let video play to 100%
                            // Completion will be marked when the 'ended' event fires

                            if (currentTime >= 3) {
                              saveVideoPosition(lesson._id, currentTime)
                              lastSavedTime = currentTime
                            }
                          }
                        }

                        // Save position when user leaves the page
                        const handleVisibilityChange = () => {
                          if (
                            document.hidden &&
                            video.currentTime &&
                            video.currentTime >= 3
                          ) {
                            saveVideoPosition(lesson._id, video.currentTime)
                          }
                        }

                        // Save position before page unload
                        const handleBeforeUnload = () => {
                          if (video.currentTime) {
                            saveVideoPosition(lesson._id, video.currentTime)
                          }
                        }

                        video.addEventListener(
                          'loadedmetadata',
                          handleLoadedMetadata
                        )
                        video.addEventListener('loadeddata', handleLoadedData)
                        video.addEventListener('canplay', handleCanPlay)
                        video.addEventListener('timeupdate', handleTimeUpdate)
                        video.addEventListener('pause', handlePause)
                        window.addEventListener(
                          'beforeunload',
                          handleBeforeUnload
                        )
                        document.addEventListener(
                          'visibilitychange',
                          handleVisibilityChange
                        )

                        // Also try to restore position when video source is set
                        if (savedPosition > 0) {
                          video.addEventListener(
                            'seeked',
                            () => {
                              if (
                                video.currentTime < savedPosition - 1 &&
                                !positionRestoredRef.current
                              ) {
                                video.currentTime = savedPosition
                                positionRestoredRef.current = true
                              }
                            },
                            { once: true }
                          )
                        }

                        return () => {
                          // Save position one last time before component unmounts
                          if (
                            video &&
                            video.currentTime &&
                            video.currentTime >= 3
                          ) {
                            saveVideoPosition(lesson._id, video.currentTime)
                          }

                          video.removeEventListener(
                            'loadedmetadata',
                            handleLoadedMetadata
                          )
                          video.removeEventListener(
                            'loadeddata',
                            handleLoadedData
                          )
                          video.removeEventListener('canplay', handleCanPlay)
                          video.removeEventListener(
                            'timeupdate',
                            handleTimeUpdate
                          )
                          video.removeEventListener('pause', handlePause)
                          window.removeEventListener(
                            'beforeunload',
                            handleBeforeUnload
                          )
                          document.removeEventListener(
                            'visibilitychange',
                            handleVisibilityChange
                          )
                          if (savePositionTimeoutRef.current) {
                            clearTimeout(savePositionTimeoutRef.current)
                          }
                          positionRestoredRef.current = false
                        }
                      }, [lesson._id, fullUrl])

                      return (
                        <video
                          ref={videoRef}
                          src={fullUrl}
                          controls
                          className="w-full h-full"
                          style={{ maxHeight: '600px' }}
                          crossOrigin="anonymous"
                          preload="metadata"
                          onEnded={() => {
                            // Mark as completed if not already done
                            if (!isLessonCompleted(lesson._id)) {
                              markLessonAsCompleted(lesson._id)
                              toast.success('Lesson completed!')
                            }

                            // Navigate to next uncompleted lesson after a delay
                            setTimeout(() => {
                              const nextLesson =
                                findNextUncompletedLesson(lessons)
                              if (nextLesson) {
                                navigate(
                                  `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                                  { replace: true }
                                )
                              }
                            }, 2000)
                          }}
                          onError={() => {
                            // Video load error - handled silently
                          }}
                          onLoadStart={() => {
                            // Video load started
                          }}
                          onLoadedData={() => {
                            // Video data loaded
                          }}
                        >
                          <source src={fullUrl} type="video/mp4" />
                          <source src={fullUrl} type="video/webm" />
                          <source src={fullUrl} type="video/ogg" />
                          <source src={fullUrl} type="video/quicktime" />
                          <source src={fullUrl} />
                          Your browser does not support the video tag.
                        </video>
                      )
                    }

                    return <VideoPlayer key={lesson._id} />
                  })()
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <div className="text-center">
                      <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">No video available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Lesson Title and Info */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#030303] mb-3">
                  {lesson.title}
                </h2>
                {lesson.description && (
                  <div className="mb-4">
                    <p
                      className={`text-[#535862] ${
                        !expandedLessonDescription ? 'line-clamp-2' : ''
                      }`}
                    >
                      {lesson.description}
                    </p>
                    {lesson.description.length > 100 && (
                      <button
                        onClick={() =>
                          setExpandedLessonDescription(
                            !expandedLessonDescription
                          )
                        }
                        className="text-xs text-[#DE3C7D] hover:text-[#c8356f] font-medium mt-1 flex items-center gap-1 transition-colors"
                      >
                        {expandedLessonDescription ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-[#535862]">
                  {lesson.videoUrl && (
                    <div className="flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      <span>{formatDuration(lesson.videoDuration)}</span>
                    </div>
                  )}
                  {!isLessonCompleted(lesson._id) && (
                    <button
                      onClick={() => {
                        markLessonAsCompleted(lesson._id)
                        toast.success('Lesson marked as completed!')
                        // Navigate to next uncompleted lesson after a short delay
                        setTimeout(() => {
                          const nextLesson =
                            findNextUncompletedLesson(lessons)
                          if (nextLesson) {
                            navigate(
                              `/student/courses/${courseId}/lessons/${nextLesson._id}`,
                              { replace: true }
                            )
                          }
                        }, 1000)
                      }}
                      className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      <span>Mark as Complete</span>
                    </button>
                  )}
                  {isLessonCompleted(lesson._id) && (
                    <div className="px-4 py-2 bg-green-100 text-green-700 rounded-md text-sm font-medium flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      <span>Completed</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section - hidden until later phase; set COMMENTS_ENABLED = true to re-enable */}
              {COMMENTS_ENABLED && (
              <div className="border-t border-gray-200 pt-6 mt-8">
                {/* Comments Header */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-[#030303] mb-1">
                    {comments.length}{' '}
                    {comments.length === 1 ? 'Comment' : 'Comments'}
                  </h3>
                </div>

                {/* Add Comment Form - YouTube Style */}
                <div className="mb-8">
                  <form onSubmit={handleSubmitComment}>
                    <div className="flex gap-3 items-start">
                      {/* User Avatar */}
                      <div className="w-10 h-10 bg-[#DE3C7D] rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {(() => {
                          const firstName = localStorage.getItem('first_name')
                          const name = localStorage.getItem('name')
                          return (firstName || name || 'U')
                            .charAt(0)
                            .toUpperCase()
                        })()}
                      </div>
                      {/* Comment Input */}
                      <div className="flex-1">
                        <div className="relative">
                          <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a public comment..."
                            className="w-full px-0 py-2 border-b-2 border-transparent focus:border-[#DE3C7D] focus:outline-none text-sm text-[#030303] placeholder-gray-400 transition-colors"
                            onFocus={(e) => {
                              e.target.style.borderBottomColor = '#DE3C7D'
                            }}
                            onBlur={(e) => {
                              if (!newComment) {
                                e.target.style.borderBottomColor = 'transparent'
                              }
                            }}
                          />
                        </div>
                        {/* Action Buttons */}
                        {newComment && (
                          <div className="flex justify-end gap-3 mt-3">
                            <button
                              type="button"
                              onClick={() => setNewComment('')}
                              className="px-4 py-2 text-sm font-medium text-[#030303] hover:bg-gray-100 rounded-full transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={
                                isSubmittingComment || !newComment.trim()
                              }
                              className="px-4 py-2 bg-[#DE3C7D] text-white text-sm font-medium rounded-full hover:bg-[#c8356f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmittingComment ? 'Posting...' : 'Comment'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                {/* Comments List - YouTube Style */}
                <div className="space-y-6" style={{ minHeight: '100px' }}>
                  {comments.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg border border-gray-200 p-6">
                      <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-[#535862] text-lg font-medium">
                        No comments yet. Be the first to comment!
                      </p>
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment._id} className="flex gap-3 items-start">
                        {/* User Avatar */}
                        <div className="w-10 h-10 bg-[#DE3C7D] rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {comment.student_id?.first_name?.[0] || 'U'}
                        </div>
                        {/* Comment Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <p className="font-medium text-[#030303] text-sm leading-tight">
                                {comment.student_id?.first_name}{' '}
                                {comment.student_id?.last_name}
                              </p>
                              <p className="text-xs text-[#535862] mt-0.5">
                                {formatDate(comment.createdAt)}
                              </p>
                            </div>
                            {/* Admin actions */}
                            {isAdmin() && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEditClick(comment)}
                                  className="text-gray-400 hover:text-[#DE3C7D] transition-colors p-1 flex-shrink-0"
                                  title="Edit comment"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteComment(comment._id)
                                  }
                                  className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                                  title="Delete comment"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          <p className="text-[#030303] text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {comment.comment}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={commentsEndRef} />
                </div>
              </div>
              )}

              {/* Edit Comment Modal - Admin Only (hidden when COMMENTS_ENABLED is false) */}
              {COMMENTS_ENABLED && isAdmin() && (
                <Modal
                  isOpen={editModalOpen}
                  onClose={() => {
                    setEditModalOpen(false)
                    setCommentToEdit(null)
                    setEditedCommentText('')
                  }}
                  title="Edit Comment"
                >
                  <div className="p-4">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Student: {commentToEdit?.student_id?.first_name}{' '}
                        {commentToEdit?.student_id?.last_name}
                      </label>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lesson: {lesson?.title || 'N/A'}
                      </label>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Comment
                      </label>
                      <textarea
                        value={editedCommentText}
                        onChange={(e) => setEditedCommentText(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE3C7D] focus:border-transparent"
                        placeholder="Enter comment text..."
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setEditModalOpen(false)
                          setCommentToEdit(null)
                          setEditedCommentText('')
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        disabled={isSaving}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={isSaving || !editedCommentText.trim()}
                        className="px-4 py-2 bg-[#DE3C7D] text-white rounded-lg hover:bg-[#c2356a] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </Modal>
              )}
            </div>
          ) : lessons.length === 0 ? (
            <div className="p-6">
              <div
                className="bg-black rounded-lg overflow-hidden mb-6 flex items-center justify-center"
                style={{ aspectRatio: '16/9', minHeight: '400px' }}
              >
                <div className="text-center text-white">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg mb-2">No lessons available</p>
                  <p className="text-sm opacity-75">
                    Lessons will appear here once they are published by your
                    instructor.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div
                className="bg-black rounded-lg overflow-hidden mb-6 flex items-center justify-center"
                style={{ aspectRatio: '16/9', minHeight: '400px' }}
              >
                <div className="text-center text-white">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select a lesson to view</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Side - Lesson List */}
        <div className="lg:w-1/3 bg-white border-l border-[#F0F0F0]">
          <div className="p-6 sticky top-[88px] max-h-[calc(100vh-88px)] overflow-y-auto">
            {/* Course Info */}
            <div className="mb-6 pb-6 border-b border-[#F0F0F0]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-[#030303]">
                  {course.title}
                </span>
              </div>
              <p className="text-xs text-[#535862] mb-3">Course</p>
              <div className="flex items-center gap-4 text-sm text-[#535862] mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span>
                    {lessons.length} Lesson{lessons.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Retake Course Button - Show when all lessons are completed */}
              {areAllLessonsCompleted() && (
                <button
                  onClick={() => setRetakeModalOpen(true)}
                  className="w-full px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  <span>Retake Course</span>
                </button>
              )}
            </div>

            {/* Lessons List - grouped by category */}
            <div>
              <h3 className="text-lg font-semibold text-[#030303] mb-4">
                Course Content
              </h3>
              {sortedLessons.length === 0 ? (
                <div className="text-center py-8 text-[#535862]">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm font-medium mb-1">
                    No lessons available
                  </p>
                  <p className="text-xs text-[#535862]">
                    Lessons will appear here once they are published by your
                    instructor.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {sections.map((section) => (
                    <div key={section.id ?? 'uncategorized'} className="border-b border-[#F0F0F0] pb-4 last:border-0">
                      <div className="flex items-center gap-1.5 mb-2">
                        <FolderOpen className="w-4 h-4 text-[#DE3C7D] flex-shrink-0" />
                        <span className="text-sm font-semibold text-[#030303]">
                          {section.title}
                          {section.lessons.length > 0 && (
                            <span className="text-[#535862] font-normal"> ({section.lessons.length})</span>
                          )}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {section.lessons.map((lessonItem) => {
                          const globalIndex = sortedLessons.findIndex((l) => l._id === lessonItem._id)
                          const isSelected = lesson?._id === lessonItem._id
                          const isCompleted = isLessonCompleted(lessonItem._id)
                          const userRole = getCurrentUserRole()
                          return (
                            <div
                              key={lessonItem._id}
                              onClick={() => handleLessonClick(lessonItem, globalIndex)}
                              className={`group p-3 rounded-lg transition-all relative ${
                                isSelected
                                  ? 'bg-[#DE3C7D] text-white cursor-pointer'
                                  : 'hover:bg-neutral-50 text-[#030303] cursor-pointer'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                  {isCompleted &&
                                    userRole !== 'admin' &&
                                    userRole !== 'subadmin' && (
                                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    )}
                                  <span
                                    className={`text-sm font-medium flex-shrink-0 ${
                                      isSelected ? 'text-white' : 'text-[#535862]'
                                    }`}
                                  >
                                    #{globalIndex + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <h4
                                      className={`text-sm font-medium mb-1 truncate ${
                                        isSelected ? 'text-white' : 'text-[#030303]'
                                      }`}
                                    >
                                      {lessonItem.title}
                                    </h4>
                                    {lessonItem.videoUrl && (
                                      <div
                                        className={`flex items-center gap-1 text-xs ${
                                          isSelected
                                            ? 'text-white/80'
                                            : 'text-[#535862]'
                                        }`}
                                      >
                                        <Play className="w-3 h-3" />
                                        <span>
                                          {formatDuration(lessonItem.videoDuration)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Retake Course Confirmation Modal */}
      <Modal
        isOpen={retakeModalOpen}
        onClose={() => !isResetting && setRetakeModalOpen(false)}
        title="Retake Course"
      >
        <div className="p-4">
          <div className="mb-6">
            <p className="text-[#535862] text-base leading-relaxed">
              Are you sure you want to retake this course? This will reset all
              your progress and you'll need to complete all lessons again.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setRetakeModalOpen(false)}
              disabled={isResetting}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={retakeCourse}
              disabled={isResetting}
              className="px-4 py-2 bg-[#DE3C7D] text-white rounded-lg hover:bg-[#c2356a] transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isResetting ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Resetting...</span>
                </>
              ) : (
                <>
                  <BookOpen className="w-4 h-4" />
                  <span>Retake Course</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default StudentLessonView
